import { useQuery } from '@tanstack/react-query';

import { queryClient } from '../components/queryClient';
import { EventsFilter, Fleet } from '../interfaces';
import { setQueryStringValue, useFleet } from '../hooks';
import {
  defaultFilter,
  EVENT_FILTER_DEBOUNCE_TIME_FAST,
  FILTER_QUERY_PARAM_KEY,
  fleetCleanFilters,
  fleetDefaultFilters,
} from '../constants';
import { AnalyticsService } from '../services';
import { Debounce, deepCopy } from '../utils';
import { getCurrentFleetID } from './fleets';

const getFilterQueryKey = (fleetUUID: string | null) => ['filter', fleetUUID];

const debouncer = new Debounce(EVENT_FILTER_DEBOUNCE_TIME_FAST);

export const useEventsFilter = () => {
  const fleet = useFleet();
  const queryKey = getFilterQueryKey(fleet.uuid);
  const queryStringFilter = new URLSearchParams(window.location.search).get(FILTER_QUERY_PARAM_KEY);

  let filterFromUrl: EventsFilter = {};
  try {
    filterFromUrl = JSON.parse(queryStringFilter || '{}');
  } catch {
    filterFromUrl = {};
  }
  const fleetDefaultFilter = fleetDefaultFilters(fleet.uuid);

  let filter = deepCopy(fleetDefaultFilter) as EventsFilter;

  // If we have a filter in the URL, use that instead
  if (filterFromUrl && Object.keys(filterFromUrl).length > 0) {
    filter = { ...filterFromUrl };
  }

  // Sub missing values for defaults
  const baseFilter = defaultFilter();
  Object.keys(baseFilter).forEach((key) => {
    const typedKey = key as keyof EventsFilter;
    if (filter[typedKey] === undefined) {
      filter[typedKey] = fleetDefaultFilter[typedKey] ?? baseFilter[typedKey];
    }
  });

  const queryFn = async () => filter;

  return useQuery({
    initialData: filter,
    queryKey,
    queryFn,
    cacheTime: Infinity, // 24h
    staleTime: Infinity, // 24h
    refetchOnWindowFocus: false,
  });
};

export const setEventsFilter = (filter: EventsFilter) => {
  // Put data (or partial data) into cache
  queryClient.setQueryData(getFilterQueryKey(getCurrentFleetID()), filter);

  debouncer.debounce(() => {
    const filterString = JSON.stringify(filter);
    setQueryStringValue(FILTER_QUERY_PARAM_KEY, filterString, true);
  });
};

export const updateFilterPartial = (filter: Partial<EventsFilter>) => {
  const oldFilter = queryClient.getQueryData<EventsFilter>(getFilterQueryKey(getCurrentFleetID())) ?? {};
  const newFilter = { ...oldFilter, ...filter };
  setEventsFilter(newFilter);
};

export const getFilterData = (): EventsFilter | undefined => {
  return queryClient.getQueryData<EventsFilter>(getFilterQueryKey(getCurrentFleetID()));
};

export function setFilter(filter: EventsFilter) {
  AnalyticsService.event('filter', 'update', 'filter');
  setEventsFilter(filter);
}

export function setFilterClean(fleetUUID: Fleet['uuid']) {
  setFilter(fleetCleanFilters(fleetUUID));
}

export function setDefaultFilter(fleetUUID: Fleet['uuid']) {
  AnalyticsService.event('filter', 'update', 'filter');
  setEventsFilter(fleetDefaultFilters(fleetUUID));
}
